/**
 * Article Detail Page - 아티클 상세 페이지
 * Firestore 기반 자동 발행 콘텐츠 상세 보기
 * 
 * Toss 스타일 깔끔한 읽기 경험
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../../firebase/config';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './ArticleDetailPage.css';
// 📊 UTM 공유 링크 유틸리티
import { getCopyShareUrl } from '../../utils/analytics';
import { useToast } from '../../components/Toast';

const ArticleDetailPage = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [article, setArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // 아티클 가져오기
  useEffect(() => {
    const fetchArticle = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const docRef = doc(db, 'blog_posts', articleId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setArticle({ id: docSnap.id, ...data });
          
          // 조회수 증가
          try {
            await updateDoc(docRef, {
              view_count: increment(1)
            });
          } catch (e) {
            // 조회수 증가 실패는 무시
          }
        } else {
          setError('아티클을 찾을 수 없습니다.');
        }
      } catch (err) {
        console.error('아티클 로드 실패:', err);
        setError('콘텐츠를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    if (articleId) {
      fetchArticle();
    }
  }, [articleId]);

  // 날짜 포맷
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // 읽기 시간 계산
  const calculateReadTime = (content) => {
    if (!content) return '1분';
    const wordCount = content.length;
    const minutes = Math.ceil(wordCount / 500);
    return `${minutes}분`;
  };


  // 공유하기
  const handleShare = async () => {
    // 📊 UTM 파라미터 추가 (트래픽 추적용)
    const shareUrl = getCopyShareUrl(window.location.href, 'article_share');
    
    const shareData = {
      title: article?.title || '교사 인사이트',
      text: article?.excerpt || article?.metadata?.metaDescription || '',
      url: shareUrl
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (e) {
        // 취소된 경우 무시
      }
    } else {
      // 클립보드에 복사 (UTM 포함)
      try {
        await navigator.clipboard.writeText(shareUrl);
        showToast('링크를 복사했어요', 'success');
      } catch (e) {
        console.error('복사 실패:', e);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="article-detail-loading">
        <div className="article-detail-loading-spinner"></div>
        <p>콘텐츠를 불러오는 중...</p>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="article-detail-error">
        <div className="article-detail-error-content">
          <h2>콘텐츠를 찾을 수 없습니다</h2>
          <p>{error || '요청하신 아티클이 존재하지 않거나 삭제되었습니다.'}</p>
          <button onClick={() => navigate('/articles')}>목록으로 돌아가기</button>
        </div>
      </div>
    );
  }

  const title = article.title || article.metadata?.seoTitle || '교사 인사이트';
  const description = article.excerpt || article.metadata?.metaDescription || '';
  const keywords = article.metadata?.hashtags?.join(', ') || article.keywords?.join(', ') || '';
  const canonicalUrl = `https://whatquiz.co.kr/articles/${articleId}`;
  const publishedDate = article.published_at?.toDate?.()?.toISOString() || new Date().toISOString();
  const modifiedDate = article.updated_at?.toDate?.()?.toISOString() || publishedDate;
  
  // OG 이미지 (기존 왓퀴즈 OG 이미지 사용)
  const ogImage = article.thumbnail || article.metadata?.ogImage || 'https://whatquiz.co.kr/og-image-whatquiz-2025.png';
  
  // 단어 수 계산
  const wordCount = article.content?.length || 0;

  return (
    <div className="article-detail-page">
      <Helmet>
        <title>{title} | 왓퀴즈</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="왓퀴즈" />
        <meta property="og:locale" content="ko_KR" />
        <meta property="article:published_time" content={publishedDate} />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="article:author" content="왓퀴즈" />
        <meta property="article:section" content={article.category || '교육'} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />
        
        {/* Article 구조화 데이터 */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": title,
            "description": description,
            "image": ogImage,
            "author": {
              "@type": "Organization",
              "name": "왓퀴즈",
              "url": "https://whatquiz.co.kr"
            },
            "publisher": {
              "@type": "Organization",
              "name": "왓퀴즈",
              "url": "https://whatquiz.co.kr",
              "logo": {
                "@type": "ImageObject",
                "url": "https://whatquiz.co.kr/logo.png"
              }
            },
            "datePublished": publishedDate,
            "dateModified": modifiedDate,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": canonicalUrl
            },
            "articleSection": article.category || '교육',
            "wordCount": wordCount,
            "keywords": keywords
          })}
        </script>
        
        {/* BreadcrumbList 구조화 데이터 */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "홈",
                "item": "https://whatquiz.co.kr"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "인사이트",
                "item": "https://whatquiz.co.kr/articles"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": title
              }
            ]
          })}
        </script>
      </Helmet>


      {/* 아티클 헤더 */}
      <header className="article-detail-header">
        <div className="article-detail-header-content">
          <div className="article-detail-meta">
            <span className="article-detail-category">{article.category}</span>
            <span className="article-detail-dot">·</span>
            <span className="article-detail-date">{formatDate(article.published_at)}</span>
            <span className="article-detail-dot">·</span>
            <span className="article-detail-readtime">{calculateReadTime(article.content)} 읽기</span>
          </div>
          <h1 className="article-detail-title">{title}</h1>
          {description && (
            <p className="article-detail-excerpt">{description}</p>
          )}
          {article.metadata?.hashtags && (
            <div className="article-detail-tags">
              {article.metadata.hashtags.slice(0, 5).map((tag, idx) => (
                <span key={idx} className="article-detail-tag">#{tag}</span>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* 아티클 본문 */}
      <article className="article-detail-content">
        <div className="article-detail-body">
          {/* eslint-disable jsx-a11y/heading-has-content, jsx-a11y/anchor-has-content */}
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({node, children, ...props}) => <h2 className="article-h1" {...props}>{children}</h2>,
              h2: ({node, children, ...props}) => {
                // 참고 자료 섹션에 id 부여 (각주 클릭 시 스크롤 대상)
                const text = String(children);
                const isReferences = text.includes('참고 자료') || text.includes('참고자료');
                return (
                  <h2 
                    className="article-h2" 
                    id={isReferences ? 'references-section' : undefined}
                    {...props}
                  >
                    {children}
                  </h2>
                );
              },
              h3: ({node, children, ...props}) => <h3 className="article-h3" {...props}>{children}</h3>,
              p: ({node, children, ...props}) => <p className="article-p" {...props}>{children}</p>,
              ul: ({node, children, ...props}) => <ul className="article-ul" {...props}>{children}</ul>,
              ol: ({node, children, ...props}) => <ol className="article-ol" {...props}>{children}</ol>,
              li: ({node, children, ...props}) => <li className="article-li" {...props}>{children}</li>,
              a: ({node, children, href, ...props}) => {
                // 각주 링크 (#references-section) - 참고 자료 섹션으로 스크롤
                if (href && href.startsWith('#')) {
                  return (
                    <a 
                      className="article-citation-link"
                      href={href}
                      onClick={(e) => {
                        e.preventDefault();
                        const targetId = href.substring(1);
                        const targetElement = document.getElementById(targetId);
                        if (targetElement) {
                          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }}
                      title="참고 자료 보기"
                      {...props}
                    >
                      {children}
                    </a>
                  );
                }
                // whatquiz 링크면 홈으로 redirect + UTM 유지 (페이지 새로고침으로 분석 이벤트 트리거)
                if (href && (href.includes('whatquiz') || href.startsWith('/'))) {
                  const topicId = article.topic_id || articleId;
                  const utmParams = `utm_source=whatquiz_blog&utm_medium=organic&utm_campaign=daily_content&utm_content=${topicId}`;
                  const homeUrl = `/?${utmParams}`;
                  return (
                    <a 
                      className="article-link" 
                      href={homeUrl}
                      onClick={(e) => {
                        e.preventDefault();
                        // 실제 페이지 이동으로 UTM 분석 이벤트 트리거
                        window.location.href = homeUrl;
                      }}
                      {...props}
                    >
                      {children}
                    </a>
                  );
                }
                // 그 외 외부 링크는 새 탭에서
                return <a className="article-link" href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
              },
              blockquote: ({node, children, ...props}) => <blockquote className="article-quote" {...props}>{children}</blockquote>,
              code: ({node, inline, children, ...props}) => 
                inline 
                  ? <code className="article-inline-code" {...props}>{children}</code>
                  : <pre className="article-code-block"><code {...props}>{children}</code></pre>,
              strong: ({node, children, ...props}) => <strong className="article-strong" {...props}>{children}</strong>,
              // 표 스타일
              table: ({node, children, ...props}) => <table className="article-table" {...props}>{children}</table>,
              thead: ({node, children, ...props}) => <thead {...props}>{children}</thead>,
              tbody: ({node, children, ...props}) => <tbody {...props}>{children}</tbody>,
              tr: ({node, children, ...props}) => <tr {...props}>{children}</tr>,
              th: ({node, children, ...props}) => <th {...props}>{children}</th>,
              td: ({node, children, ...props}) => <td {...props}>{children}</td>,
            }}
          >
            {/* AI 생성 마크다운 기호 정리 - 특수문자 + ** 조합 처리 */}
            {(article.content
              // 1. 특수문자가 ** 안에 있는 경우 → 특수문자를 밖으로 이동
              ?.replace(/\*\*'([^'*]+)'\*\*/g, "'**$1**'")  // **'텍스트'** → '**텍스트**'
              ?.replace(/\*\*"([^"*]+)"\*\*/g, '"**$1**"')  // **"텍스트"** → "**텍스트**"
              ?.replace(/\*\*\(([^)*]+)\)\*\*/g, '(**$1**)')  // **(텍스트)** → (**텍스트**)
              ?.replace(/\*\*\[([^\]*]+)\]\*\*/g, '[**$1**]')  // **[텍스트]** → [**텍스트**]
              // 2. 복합 패턴 처리 (따옴표+괄호 조합)
              ?.replace(/\*\*'([^']+)\(([^)]+)\)'\*\*/g, "'**$1($2)**'")  // **'텍스트(괄호)'** → '**텍스트(괄호)**'
              ?.replace(/\*\*'([^']+)'\*\*을/g, "'**$1**'을")  // **'텍스트'**을 → '**텍스트**'을
              ?.replace(/\*\*'([^']+)'\*\*이/g, "'**$1**'이")  // **'텍스트'**이 → '**텍스트**'이
              // 3. 기존 처리 (링크, 조사)
              ?.replace(/\*\*\[([^\]]+)\]\(([^)]+)\)\*\*/g, '[$1]($2)')  // **[텍스트](URL)** → [텍스트](URL)
              ?.replace(/\*\*([^*]+)\*\*에게/g, '$1에게')  // **왓퀴즈**에게 → 왓퀴즈에게
              ?.replace(/\*\*([^*]+)\*\*를/g, '$1를')  // **왓퀴즈**를 → 왓퀴즈를
              ?.replace(/\*\*([^*]+)\*\*의/g, '$1의')  // **왓퀴즈**의 → 왓퀴즈의
            )}
          </ReactMarkdown>
        </div>
      </article>

      {/* CTA 섹션 */}
      <section className="article-detail-cta">
        <div className="article-detail-cta-content">
          <h3>이 글이 도움이 되셨나요?</h3>
          <p>왓퀴즈로 더 많은 업무를 자동화해보세요</p>
          <div className="article-detail-cta-buttons">
            <button 
              className="article-detail-cta-primary"
              onClick={() => {
                // UTM 파라미터 포함하여 홈페이지로 이동 (페이지 새로고침으로 분석 이벤트 트리거)
                const topicId = article.topic_id || articleId;
                const utmParams = `?utm_source=whatquiz_blog&utm_medium=organic&utm_campaign=daily_content&utm_content=${topicId}`;
                window.location.href = `/${utmParams}`;
              }}
            >
              무료로 시작하기
            </button>
            <button 
              className="article-detail-cta-secondary"
              onClick={() => navigate('/articles')}
            >
              다른 글 보기
            </button>
          </div>
        </div>
      </section>

      {/* 하단 정보 */}
      <footer className="article-detail-footer">
        {article.view_count > 0 && (
          <span className="article-detail-views">
            {article.view_count.toLocaleString()}명이 읽었습니다
          </span>
        )}
        <button 
          className="article-detail-share-small"
          onClick={handleShare}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
          공유
        </button>
      </footer>
    </div>
  );
};

export default ArticleDetailPage;
